import {
  Fragment,
  createContext,
  use,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useInventionNames } from "@/hooks/use-invention";
import type {
  InventionElementResponse,
  TermResponse,
} from "@patent-studio/api-types";
import { FieldLabel } from "@/components/ui/FieldLabel";
import { CAPTION_LINE } from "@/components/ui/marks";
import { StatusPill } from "@/components/ui/StatusPill";
import {
  REFERENCE_TINT,
  StructuredText,
  type ReferenceSource,
} from "@/components/ui/StructuredText";
import { textOf, type TextBlock } from "@/lib/structured-text";
import { cn } from "@/lib/utils";
import { listed } from "@/lib/plural";

/**
 * The term of each element and rung the page draws, by the id of the thing it
 * names. Read once for the page and handed down, since a rung sits three
 * components below the list that reads it.
 */
const TermsContext = createContext<ReadonlyMap<string, TermResponse>>(
  new Map(),
);

/** The terms of an invention's elements and rungs, by the id of what each names. */
function useTermsOf(taskId: string | null): ReadonlyMap<string, TermResponse> {
  const { data } = useInventionNames(taskId);
  return useMemo(
    () => new Map((data?.terms ?? []).map((term) => [term.thing_id, term])),
    [data],
  );
}

/** The term of one element or rung, if it has one. */
export function useTerm(thingId: string): TermResponse | undefined {
  return use(TermsContext).get(thingId);
}

/** How the page names an element: by its number while it has one. */
export function called(element: InventionElementResponse): string {
  return element.number === null
    ? `the element "${element.text}"`
    : `element ${String(element.number)}`;
}

/**
 * How the page names each element and fallback, by the id of the thing, for a
 * row that names another: a term two things share says which the other is.
 */
const ThingLabelsContext = createContext<ReadonlyMap<string, string>>(
  new Map(),
);

/** Each element and fallback of the set, named as the page names it. */
function thingLabels(
  elements: readonly InventionElementResponse[],
): ReadonlyMap<string, string> {
  const labels = new Map<string, string>();
  for (const element of elements) {
    labels.set(element.element_id, called(element));
    for (const rung of element.fallbacks)
      labels.set(
        rung.fallback_id,
        `the fallback "${rung.text}" of ${called(element)}`,
      );
  }
  return labels;
}

/**
 * The page's names, handed down to every row: each thing's term, and how the
 * page names each element and fallback, for a term two things share.
 */
export function PageNames({
  taskId,
  elements,
  children,
}: {
  taskId: string | null;
  elements: readonly InventionElementResponse[];
  children: ReactNode;
}) {
  const terms = useTermsOf(taskId);
  const labels = useMemo(() => thingLabels(elements), [elements]);
  return (
    <TermsContext value={terms}>
      <ThingLabelsContext value={labels}>{children}</ThingLabelsContext>
    </TermsContext>
  );
}

/**
 * A term another element or fallback also bears, said on the row with what to
 * do about it. The name saved as the attorney wrote it; this asks for another
 * and never holds the save back.
 */
function SharedName({ term }: { term: TermResponse | undefined }) {
  const labels = use(ThingLabelsContext);
  if (term === undefined || term.shared_with.length === 0) return null;
  const others = term.shared_with.map((id) => labels.get(id) ?? "another");
  return (
    <p
      data-testid={`shared-name-${term.thing_id}`}
      className={cn("flex flex-wrap items-center gap-1.5", CAPTION_LINE)}
    >
      <StatusPill tone="warning" size="sm" plain>
        Shared name
      </StatusPill>
      <span>
        Also the name of {listed(others)}: give one of them another name
      </span>
    </p>
  );
}

/**
 * One stretch of the words drawn as something: where it sits, and how it is
 * drawn. It sits by characters, as the server counts them, and not by the
 * UTF-16 units a script string counts, which differ where a character lies
 * outside the basic plane.
 */
export interface WordsMark {
  start: number;
  end: number;
  draw: (words: string) => ReactNode;
}

/** The term as a mark on its words, or nothing where it is not words of them. */
function termMark(term: TermResponse | undefined): WordsMark | null {
  if (term?.start == null || term.end == null) return null;
  return {
    start: term.start,
    end: term.end,
    draw: (words) => (
      <span data-term={term.term} className={REFERENCE_TINT}>
        {words}
      </span>
    ),
  };
}

/**
 * The marks drawn on the words: the term's, where it is words of them, and the
 * caller's, in the order they stand. Where two overlap, the one that starts
 * first is drawn and the other is left out, so a caller whose mark is left out
 * says what it marks another way.
 */
export function drawnMarks(
  term: TermResponse | undefined,
  marks: readonly WordsMark[],
): WordsMark[] {
  const first = termMark(term);
  const ordered = [...(first === null ? [] : [first]), ...marks].sort(
    (one, other) => one.start - other.start,
  );
  const drawn: WordsMark[] = [];
  let at = 0;
  for (const mark of ordered) {
    if (mark.start < at) continue;
    drawn.push(mark);
    at = mark.end;
  }
  return drawn;
}

/**
 * The words with the term marked where it sits in them, as a claim's named
 * stretch is marked, and any other marks the caller draws, such as the words
 * that say how the element attaches. A term that is not words of the text marks
 * nothing here; `ClaimedAs` says what it is. Which marks are drawn where two
 * overlap is `drawnMarks`'s to say.
 */
export function MarkedWords({
  text,
  term,
  marks = [],
}: {
  text: string;
  term: TermResponse | undefined;
  marks?: readonly WordsMark[];
}) {
  const characters = Array.from(text);
  const words = (start: number, end?: number) =>
    characters.slice(start, end).join("");
  const drawn: ReactNode[] = [];
  let at = 0;
  for (const mark of drawnMarks(term, marks)) {
    drawn.push(
      <Fragment key={mark.start}>
        {words(at, mark.start)}
        {mark.draw(words(mark.start, mark.end))}
      </Fragment>,
    );
    at = mark.end;
  }
  return (
    <>
      {drawn}
      {words(at)}
    </>
  );
}

/**
 * A term that is not words of the text, as the attorney wrote it, with what
 * that means: the novelty run read the words, not this, so it is not assessed
 * at this breadth.
 */
function ClaimedAs({ term }: { term: TermResponse | undefined }) {
  if (term === undefined || !term.not_assessed) return null;
  return (
    <span
      className={cn("inline-flex flex-wrap items-center gap-1.5", CAPTION_LINE)}
    >
      <span>Claimed as &ldquo;{term.term}&rdquo;</span>
      <StatusPill tone="warning" size="sm" plain>
        Not assessed at this breadth
      </StatusPill>
    </span>
  );
}

/**
 * What the row says of its term beyond the words: that it is claimed as
 * written, and that another element or fallback bears it too.
 */
export function TermNotes({ term }: { term: TermResponse | undefined }) {
  return (
    <>
      <ClaimedAs term={term} />
      <SharedName term={term} />
    </>
  );
}

/** A name is at most this many words, as the claim editor reads one. */
const LONGEST_TERM_WORDS = 4;

/** The most spans offered at once, so the list stays a list to choose from. */
const MOST_OFFERED = 6;

/** Every run of one to four words of the text, in the order they stand. */
function spansOf(text: string): string[] {
  const words = text.split(/[\s;:,.()]+/).filter((word) => word !== "");
  const spans: string[] = [];
  for (let at = 0; at < words.length; at += 1) {
    for (let length = 1; length <= LONGEST_TERM_WORDS; length += 1) {
      if (at + length <= words.length)
        spans.push(words.slice(at, at + length).join(" "));
    }
  }
  return [...new Set(spans)];
}

/**
 * Where the words typed are matched: against the thing's own words, so what is
 * chosen is a span of them. Words typed and not chosen are the attorney's own,
 * and are claimed as written.
 */
function spansSource(text: string): ReferenceSource {
  const spans = spansOf(text);
  return {
    suggest: (phrase) => {
      const typed = phrase.trim().toLowerCase();
      if (typed === "") return [];
      return (
        spans
          .filter((span) => span.toLowerCase().startsWith(typed))
          .slice(0, MOST_OFFERED)
          // The span is its own reference: each is offered once, so each is
          // told apart from the others by its words.
          .map((span) => ({ reference: span, label: span }))
      );
    },
    tagOf: () => null,
  };
}

const EMPTY: TextBlock[] = [{ runs: [], depth: 0, numbered: false }];

/** What a field that marks words of the text says while it is empty. */
const SPAN_PROMPT = "Type words from the text above";

/**
 * A field that marks a span of a thing's words: the words typed are offered
 * the spans of its text that begin with them, as the claim editor offers the
 * drawn parts. Optional where, left empty, what it marks stays as it is; not
 * where nothing is held to stay.
 */
export function SpanField({
  caption,
  label,
  text,
  optional = true,
  onChange,
}: {
  caption: string;
  label: string;
  text: string;
  optional?: boolean;
  onChange: (words: string) => void;
}) {
  const [blocks, setBlocks] = useState<TextBlock[]>(EMPTY);
  const features = useMemo(
    () => ({
      references: spansSource(text),
      capacity: { blocks: 1, refused: () => undefined },
      placeholder: SPAN_PROMPT,
    }),
    [text],
  );
  return (
    <div className="mt-2">
      <FieldLabel optional={optional}>{caption}</FieldLabel>
      <StructuredText
        label={label}
        value={blocks}
        onChange={(next) => {
          setBlocks(next);
          onChange(next.map(textOf).join(" ").trim());
        }}
        features={features}
      />
    </div>
  );
}

/** The field that marks another term for an element or a rung. */
export function TermField({
  label,
  text,
  onChange,
}: {
  label: string;
  text: string;
  onChange: (term: string) => void;
}) {
  return (
    <SpanField
      caption="Mark another term"
      label={label}
      text={text}
      onChange={onChange}
    />
  );
}
